import { loadNativeModule, pickMethod, pickProperty, constructWithFallback, describeError } from './loadNativeModule';

// Observed on a CL435 ("cobra" family, BOS 9.1.118.4): 'hdmi 1' throws "Invalid port
// name". BrightSign's docs list 'hdmi 1'..'hdmi 4' for other models, so try the plain
// name first (confirmed working on that device) and fall back to the documented form.
const PORT_CANDIDATES = ['hdmi', 'hdmi 1', 'hdmi 2'];

export async function getDisplayStatus () {
    const VideoOutput = loadNativeModule('@brightsign/videooutput');
    if (!VideoOutput) return { available: false, reason: 'videooutput module unavailable' };

    try {
        const { instance } = constructWithFallback(VideoOutput, PORT_CANDIDATES);
        const isAttached = pickMethod(instance, ['isattached', 'isAttached']);
        const getHdcpStatus = pickMethod(instance, ['gettxhdcpstatus', 'getTxHdcpStatus']);
        const getEdidIdentity = pickMethod(instance, ['getedididentity', 'getEdidIdentity']);
        const getOutputStatus = pickMethod(instance, ['getoutputstatus', 'getOutputStatus']);

        if (!isAttached || !getHdcpStatus) {
            return { available: false, reason: 'videooutput getters unavailable on this firmware' };
        }

        // Some getters on this SDK are synchronous, some return a Promise, and it isn't
        // consistent across firmware/models (isattached/gettxhdcpstatus were plain
        // booleans on a CL435, but getedididentity returned a Promise on the same
        // device). `await` on a non-Promise value just resolves immediately, so this is
        // safe either way without branching on the return type.
        const attached = Boolean(await isAttached());

        // HDCP, EDID and output-status are three independent calls on the same instance
        // once attachment is known — fire them concurrently instead of one after another.
        const [hdcpAuthenticated, edidIdentity, outputStatus] = await Promise.all([
            attached ? getHdcpStatus() : false,
            attached && getEdidIdentity ? getEdidIdentity() : null,
            attached && getOutputStatus ? getOutputStatus() : null
        ]);

        // getedididentity() resolves to a structured object on this firmware (manufacturer,
        // monitorName, HDR support flags, ...), not the bare hex string BrightSign's docs
        // describe — pull out only what's useful for support to see at a glance.
        const edidReadable = attached && getEdidIdentity ? Boolean(edidIdentity) : null;
        const edidManufacturer = edidIdentity && typeof edidIdentity === 'object' ? edidIdentity.manufacturer || null : null;
        const edidMonitorName = edidIdentity && typeof edidIdentity === 'object' ? edidIdentity.monitorName || null : null;
        // Same casing inconsistency as the method names above: BrightSign's docs list
        // this lowercase ("outputpowered"), but a Codex PR review caught that reading
        // only that casing means the property is never found on real firmware — try
        // both.
        const outputPowered = outputStatus ? Boolean(pickProperty(outputStatus, ['outputpowered', 'outputPowered'])) : null;

        return { available: true, attached, hdcpAuthenticated: Boolean(hdcpAuthenticated), edidReadable, edidManufacturer, edidMonitorName, outputPowered };
    } catch (error) {
        return { available: false, reason: `videooutput threw: ${describeError(error)}` };
    }
}
